/* Server copy of the watchlist (table `user_watchlists`) so the Telegram bot
   can send a watchlist digest. localStorage stays the source of truth for the
   UI: every call here is best-effort, never throws and never surfaces errors
   to the user (the table may not exist yet). The Supabase client is passed in
   so the module stays testable without a network. */

export const WATCHLIST_TABLE = 'user_watchlists';
export const WATCHLIST_MAX = 20;
export const COIN_ID_RE = /^[a-z0-9-]{1,80}$/;
export const SYNC_DELAY_MS = 1500;

/** Keep only valid CoinGecko ids, de-duplicated, at most 20, order preserved. */
export function sanitizeCoinIds(ids) {
  if (!Array.isArray(ids)) return [];
  const out = [];
  for (const id of ids) {
    if (typeof id !== 'string' || !COIN_ID_RE.test(id) || out.includes(id)) continue;
    out.push(id);
    if (out.length >= WATCHLIST_MAX) break;
  }
  return out;
}

/** The user's server row as a sanitized id list, or null (no row / error). */
export async function fetchServerWatchlist(client, userId) {
  if (!client || !userId) return null;
  try {
    const { data, error } = await client
      .from(WATCHLIST_TABLE)
      .select('coin_ids')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data || !Array.isArray(data.coin_ids)) return null;
    return sanitizeCoinIds(data.coin_ids);
  } catch {
    return null;
  }
}

/** Upsert the user's row. Resolves true on success, false on any failure. */
export async function upsertServerWatchlist(client, userId, ids) {
  if (!client || !userId) return false;
  try {
    const { error } = await client
      .from(WATCHLIST_TABLE)
      .upsert(
        { user_id: userId, coin_ids: sanitizeCoinIds(ids), updated_at: new Date().toISOString() },
        { onConflict: 'user_id' },
      );
    return !error;
  } catch {
    return false;
  }
}

/**
 * Decide the watchlist after sign-in.
 * - local list present → keep it (no merge) and push it to the server
 * - local empty, server has ids → restore from the server (second device)
 * - both empty → nothing to do
 * Returns { ids, source: 'local' | 'server' | 'none', push }.
 */
export async function reconcileWatchlist({ client, userId, localIds }) {
  const local = Array.isArray(localIds) && localIds.length > 0 ? localIds : null;
  if (local) return { ids: local, source: 'local', push: true };
  const server = await fetchServerWatchlist(client, userId);
  if (server && server.length > 0) return { ids: server, source: 'server', push: false };
  return { ids: null, source: 'none', push: false };
}

/**
 * Debounced uploader: rapid changes within `delayMs` collapse into a single
 * upsert carrying the latest list. A change of user drops the pending one.
 */
export function createWatchlistSyncer({ client, delayMs = SYNC_DELAY_MS } = {}) {
  let timer = null;
  let pending = null; // { userId, ids }

  function fire() {
    timer = null;
    const job = pending;
    pending = null;
    if (!job) return Promise.resolve(false);
    return upsertServerWatchlist(client, job.userId, job.ids);
  }

  return {
    schedule(userId, ids) {
      if (!userId) return;
      pending = { userId, ids: sanitizeCoinIds(ids) };
      if (timer) clearTimeout(timer);
      timer = setTimeout(fire, delayMs);
    },
    /** Send the pending change now (e.g. on pagehide). */
    flush() {
      if (timer) clearTimeout(timer);
      return fire();
    },
    cancel() {
      if (timer) clearTimeout(timer);
      timer = null;
      pending = null;
    },
  };
}
